"use client"

import { useState, useEffect, useMemo, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { FlaskConical } from "lucide-react"
import {
  HeadlineHero,
  LawnPrefillBanner,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolCrossLink,
  ToolPageLoadingFallback,
  ToolPageShell,
  ToolWideRegion,
} from "@/components/tools/shared"
import { CuratedProductPicks } from "@/components/tools/shared/curated-product-picks"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import {
  LimeCalculatorForm,
  DEFAULT_LIME_FORM,
  type LimeFormState,
} from "@/components/tools/lime-calculator-form"
import { LimeResultCard, LimeResultDetails } from "@/components/tools/lime-result-card"
import { calculateLime } from "@/lib/lime-calculator/calculator"
import { getLimePicks } from "@/lib/lime-calculator/product-picks"
import { GRASS_AGRONOMY } from "@/lib/soil-test/agronomy-tables"
import { GRASS_KEYS, type GrassKey } from "@/lib/soil-test/types"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"

const GRASS_ALIASES: Record<string, GrassKey> = {
  kbg: "kentucky-bluegrass",
  bluegrass: "kentucky-bluegrass",
  fescue: "tall-fescue",
  tallfescue: "tall-fescue",
  finefescue: "fine-fescue",
  rye: "perennial-ryegrass",
  ryegrass: "perennial-ryegrass",
  perennialrye: "perennial-ryegrass",
  bermuda: "bermudagrass",
  zoysia: "zoysiagrass",
  staug: "st-augustinegrass",
  staugustine: "st-augustinegrass",
  bahia: "bahiagrass",
  buffalo: "buffalograss",
}

/** Map a URL/saved-lawn grass string to a canonical engine GrassKey, or null. */
function resolveGrassKey(input?: string | null): GrassKey | null {
  if (!input) return null
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "")
  const target = norm(input)
  for (const key of GRASS_KEYS) {
    if (norm(key) === target) return key
    if (norm(GRASS_AGRONOMY[key].displayName) === target) return key
  }
  return GRASS_ALIASES[target] ?? null
}

function toInput(v: LimeFormState) {
  return {
    currentPh: v.currentPh,
    grassType: v.grassType,
    soilTexture: v.soilTexture,
    lawnSizeSqft: v.lawnSizeSqft || 0,
    magnesium: v.magnesium,
    zipCode: v.zipCode || undefined,
  }
}

function LimeCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("lime-calculator")
  const {
    lawns,
    selectedLawnId,
    setSelectedLawnId,
    prefill: lawnPrefill,
    isLoading: isLoadingLawn,
  } = useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  // Lazy-init from ?grass=/?size=/?zip= deep links (no derived-state effect).
  const [value, setValue] = useState<LimeFormState>(() => {
    const grass = resolveGrassKey(searchParams.get("grass"))
    const size = Number(searchParams.get("size"))
    const zip = searchParams.get("zip")
    const zipOk = !!zip && /^\d{5}$/.test(zip)
    return {
      ...DEFAULT_LIME_FORM,
      grassType: grass ?? DEFAULT_LIME_FORM.grassType,
      lawnSizeSqft: size && !Number.isNaN(size) ? size : DEFAULT_LIME_FORM.lawnSizeSqft,
      zipCode: zipOk ? (zip as string) : "",
      advancedOpen: zipOk,
    }
  })
  const [interacted, setInteracted] = useState(false)

  // Saved-lawn prefill: async external data, applied only before the user touches
  // anything and only when there were no URL params to honor.
  useEffect(() => {
    if (interacted || isLoadingLawn) return
    const hasUrl =
      searchParams.get("grass") || searchParams.get("size") || searchParams.get("zip")
    if (hasUrl) return
    setValue((v) => {
      const next = { ...v }
      const gk = resolveGrassKey(lawnPrefill.grassType)
      if (gk) next.grassType = gk
      if (lawnPrefill.lawnSizeSqft) next.lawnSizeSqft = lawnPrefill.lawnSizeSqft
      if (lawnPrefill.zipCode && /^\d{5}$/.test(lawnPrefill.zipCode)) {
        next.zipCode = lawnPrefill.zipCode
        next.advancedOpen = true
      }
      // Keep the same object when nothing changed: lawnPrefill is rebuilt on
      // every render, so a fresh object here would re-run this effect forever.
      const changed = (Object.keys(next) as (keyof LimeFormState)[]).some((k) => next[k] !== v[k])
      return changed ? next : v
    })
  }, [lawnPrefill, isLoadingLawn, interacted, searchParams])

  const result = useMemo(() => calculateLime(toInput(value)), [value])

  const handleChange = (next: LimeFormState) => {
    setValue(next)
    if (!interacted) {
      setInteracted(true)
      trackUsage("lime-calculator")
      const r = calculateLime(toInput(next))
      analytics.calculatorUsed("lime", {
        inputs: {
          current_ph: next.currentPh,
          grass_type: next.grassType,
          soil_texture: next.soilTexture,
          lawn_size_sqft: next.lawnSizeSqft,
          magnesium: next.magnesium,
        },
        result: {
          direction: r.direction,
          lbs_per_1000: r.lbsPer1000 ?? undefined,
          total_lbs: r.totalLbs ?? undefined,
          applications: r.applications ?? undefined,
        },
        source: "live_update",
      })
    }
  }

  const picks = getLimePicks(result.direction)
  const grassDisplay = GRASS_AGRONOMY[value.grassType].displayName

  return (
    <ToolPageShell
      breadcrumbLabel="Lawn Lime Calculator"
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Lawn Lime Calculator"
          usageCount={usageCount}
          grassType={grassDisplay}
          zipCode={value.zipCode || undefined}
        />
      }
    >
      <HeadlineHero
        eyebrow="Raises or lowers pH"
        eyebrowIcon={<FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />}
        title="How much lime per"
        accent="1,000 sq ft?"
        subtitle="Enter your soil pH, grass type, and soil to get pounds of lime (or sulfur, if your pH is high) per 1,000 sq ft, the total for your lawn, and how to split it safely."
      />

      <LawnPrefillBanner
        lawns={lawns}
        selectedLawnId={selectedLawnId}
        onSelect={(id) => {
          setSelectedLawnId(id)
          setInteracted(false)
        }}
      />

      {/* Two-column: form (left) + sticky results rail (right) */}
      <ToolWideRegion>
        <div className="grid items-start gap-6 lg:grid-cols-2 lg:gap-7">
          <LimeCalculatorForm value={value} onChange={handleChange} regionHint={result.regionHint} />

          <div className="flex flex-col gap-6 lg:sticky lg:top-24">
            <LimeResultCard
              result={result}
              lawnSizeSqft={value.lawnSizeSqft || undefined}
              zipCode={value.zipCode || undefined}
            />

            <ResultEmailCaptureCard
              source="tool_result_lime"
              toolDisplayName="lime plan"
              subcopy="Your amounts, timing, and a re-test reminder, sent to your inbox."
              grassType={grassDisplay}
              zipCode={value.zipCode || undefined}
              accountLead={{
                redirectTo: buildToolUrl("lime-calculator", {
                  grassType: grassDisplay,
                  zip: value.zipCode || undefined,
                }),
                headline: `Save my lime plan for ${grassDisplay}`,
                subcopy: "Keep this lime plan in a free account and track your lawn over time.",
                // Grass-aware (grassType + zipCode passed) so the subscribe route
                // sends the 12-month calendar. Name it, per .claude/rules/tools-pages.md.
                dividerLabel: "or get your 12-month calendar by email",
                onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_lime"),
              }}
              onCaptured={closeNewsletterModal}
            />

            <LimeResultDetails result={result} />

            {picks.length > 0 && (
              <CuratedProductPicks
                picks={picks}
                title="Matched to your result"
                description={`For your ${grassDisplay} lawn`}
                trackingSlug="tool:lime-calculator"
              />
            )}
          </div>
        </div>
      </ToolWideRegion>

      <ToolCrossLink
        className="mt-8"
        href="/tools/soil-test-analyzer"
        lead="Have a full lab soil test?"
        body="The Soil Test Analyzer reads the whole report: pH, nutrients, and CEC."
        cta="Analyze my soil test"
      />

      <RelatedTools
        className="mt-10"
        currentToolId="lime-calculator"
        prefillValues={{
          zip: value.zipCode || undefined,
          grassType: grassDisplay,
          lawnSize: value.lawnSizeSqft || undefined,
        }}
      />
    </ToolPageShell>
  )
}

export function LimeCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="lime-calculator" />}>
      <SearchParamsHydrationGate>
        <LimeCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
